<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { useToast, Toaster } from "@/components/ui/toast"
import { Pencil, Save, X, Trash } from "lucide-vue-next"
import axios from "axios"

import { useI18n } from 'vue-i18n';

const emit = defineEmits(['update'])

const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
if (token) axios.defaults.headers.common["X-CSRF-Token"] = token

// Column definition
interface ColumnDef {
  key: string
  label: string
  editable?: boolean
  required?: boolean
  type?: "text" | "number" | "date" | "color" | "select" | "boolean"
  options?: any[]
  optionsEndpoint?: string
}

const props = defineProps<{
  label: string,
  columns: ColumnDef[],
  endpoint: string,
  data: { type: Array, default: () => [] },
  enableediting: { type: Boolean, default: true },
}>()

const rows = ref<any[]>([])
const editingRow = ref<string | number | null>(null)
const editBuffer = ref<any | null>(null)
const validationErrors = ref<Record<string, string>>({})
const { toast } = useToast()

// Dynamic select options
const dynamicOptions = ref<Record<string, any[]>>({})

// Pagination
const currentPage = ref(1)
const pageSize = ref(20)

// Sorting
const sortKey = ref<string | null>(null)
const sortAsc = ref(true)

// Filtering
const searchQuery = ref("")

// Helpers for nested fields
function getNested(obj: any, path: string) {
  return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj)
}

function setNested(obj: any, path: string, value: any) {
  const keys = path.split(".")
  let o = obj
  keys.slice(0, -1).forEach(k => {
    if (!o[k]) o[k] = {}
    o = o[k]
  })
  o[keys[keys.length - 1]] = value
}

// Load data from endpoint
async function loadData() {
  if (props.endpoint == null) {
	console.log("props.data=" + JSON.stringify(props.data))
	rows.value = props.data
  } else {
    const { data } = await axios.get(props.endpoint + ".json?plain=1")
    rows.value = data
  }
}

// Load dynamic select options
async function loadDynamicOptions() {
  for (const col of props.columns) {
    if (col.type === "select" && col.optionsEndpoint) {
      const { data } = await axios.get(col.optionsEndpoint)
      dynamicOptions.value[col.key] = data
    }
  }
}

// Validation
function validateRow(row: any): boolean {
  validationErrors.value = {}
  let valid = true
  for (const col of props.columns) {
    if (col.required && (getNested(row, col.key) == null || String(getNested(row, col.key)).trim() === "")) {
      validationErrors.value[col.key] = `${col.label} is required.`
      valid = false
    }
  }
  return valid
}

// Save row
async function saveRow(row: any) {
  if (!editBuffer.value) return

  if (!validateRow(editBuffer.value)) {
    toast({ title: "Validation Error", variant: "destructive" })
    return
  }

  try {
    if (row.isNew) {
      const { data } = await axios.post(props.endpoint + ".json", editBuffer.value)
      const index = rows.value.findIndex(r => r.id === row.id)
      rows.value[index] = data
    } else {
      await axios.put(`${props.endpoint}/${row.id}.json`, editBuffer.value)
      Object.assign(row, editBuffer.value)
    }

    editingRow.value = null
    editBuffer.value = null
    toast({ title: "Saved" })
  } catch {
    toast({ title: "Error", variant: "destructive" })
  }
}

// Add row
function addRow() {
  searchQuery.value = ""
  currentPage.value = 1
  
  const tempRow: any = { 
    id: `new-${Date.now()}`, 
    isNew: true,
    _isNew: true 
  }

  for (const col of props.columns) {
    let defaultValue: any = ""
    if (col.type === "number") defaultValue = 0
    else if (col.type === "boolean") defaultValue = false
    
    setNested(tempRow, col.key, defaultValue)
  }

  rows.value.unshift(tempRow)
  
  editingRow.value = tempRow.id
  editBuffer.value = JSON.parse(JSON.stringify(tempRow))
}

// Edit row
function startEdit(row: any) {
  editingRow.value = row.id
  editBuffer.value = JSON.parse(JSON.stringify(row))
}

// Delete row
async function deleteRow(id: number | string) {
  const row = rows.value.find(r => r.id === id)
  if (row?.isNew) {
    rows.value = rows.value.filter(r => r.id !== id)
  } else {
	//const ok = await confirm(t('view.item.' + props.label + " Löschen?"))
	const ok = await confirm(t('view.item.' + props.label) + " Löschen? Kann nicht wiederrufen werden.")
	if (ok) {
		await axios.delete(`${props.endpoint}/${id}.json`)
		rows.value = rows.value.filter(r => r.id !== id)
		toast({ title: "Deleted", description: `Row ${id} removed.` })
		emit('update')
	}
  }
}

// Cancel edit
function cancelEdit(row: any) {
  if (row.isNew) {
    rows.value = rows.value.filter(r => r.id !== row.id)
  }
  editingRow.value = null
  editBuffer.value = null
  validationErrors.value = {}
}

// Sorting
function sortBy(key: string) {
  if (sortKey.value === key) sortAsc.value = !sortAsc.value
  else { sortKey.value = key; sortAsc.value = true }
}

console.log("columns=" + JSON.stringify(props.columns))

const sortedRows = computed(() => {
  if (!sortKey.value) return filteredRows.value
  return [...filteredRows.value].sort((a, b) => {
    const x = getNested(a, sortKey.value!)
    const y = getNested(b, sortKey.value!)
    if (x === y) return 0
    return (x > y ? 1 : -1) * (sortAsc.value ? 1 : -1)
  })
})

const paginatedRows = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return sortedRows.value.slice(start, start + pageSize.value)
})

const totalPages = computed(() => Math.ceil(sortedRows.value.length / pageSize.value))

// Initial load
onMounted(async () => {
  await loadDynamicOptions()
  await loadData()
})

watch(() => props.endpoint, async () => {
  await loadDynamicOptions()
  await loadData()
})

// Filtering
const columnFilters = ref<Record<string, any>>({})

function resetFilters() {
  searchQuery.value = ""
  columnFilters.value = {}
}

const filteredRows = computed(() => {
  return rows.value.filter(row => {
    // Global search
    const matchesSearch = searchQuery.value === "" || Object.entries(row).some(([key, value]) => {
      const col = props.columns.find(c => c.key === key)
      if (col?.type === "select") {
        const opts = col.options || dynamicOptions.value[col.key] || []
        const label = opts.find(o => o.id == value)?.label ?? value
        return String(label).toLowerCase().includes(searchQuery.value.toLowerCase())
      }
      return String(value ?? "").toLowerCase().includes(searchQuery.value.toLowerCase())
    })

    // Column-specific filters
    const matchesFilters = Object.entries(columnFilters.value).every(([key, filterVal]) => {
      if (filterVal === "" || filterVal == null) return true
      const col = props.columns.find(c => c.key === key)
      const value = getNested(row, key)
      if (col?.type === "select") {
        return String(value) === String(filterVal)
      }
      if (col?.type === "boolean") {
        return String(value) === String(filterVal)
      }
      return String(value ?? "").toLowerCase().includes(String(filterVal).toLowerCase())
    })

    return matchesSearch && matchesFilters
  })
})

const { t } = useI18n();

function colWidth(col) {
    return (col.width ? col.width : 50) + "px!important"
}

</script>

<template>
  <!-- Card as stable flex column -->
  <Card class="data-table-container">
    <!-- Header -->
    <div class="flex justify-between mb-4 items-center header-row">
		<Toaster />
      <h2 class="text-xl font-bold">{{ label }}</h2>
      <div class="flex gap-2 items-center">
        <Button variant="outline" @click="resetFilters">Filter zurücksetzen</Button>
        <Button
          v-if="enableediting"
          @click="addRow"
          class="bg-blue-500 text-white hover:bg-blue-600"
        >
          + Neuer Eintrag
        </Button>
      </div>
    </div>

    <!-- Scrollable table area -->
 <div class="table-wrapper" style="flex:1 1 auto; min-height:0; overflow:auto; -webkit-overflow-scrolling: touch;">
	<table  class="min-w-max border-collapse table-auto">
        <thead class="bg-gray-100">
          <!-- Column headers -->
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              class="sticky top-0 z-30 bg-gray-100 p-2 h-12 select-none border border-gray-200 relative"
              @click="sortBy(col.key)"
            >
              {{ col.label }}
              <span v-if="sortKey === col.key" class="ml-1 text-xs text-gray-500">
                {{ sortAsc ? '▲' : '▼' }}
              </span>
            </th>
            <th v-if="enableediting" class="sticky top-0 z-30 bg-gray-100 p-2 h-12 select-none border border-gray-200 relative">Aktionen</th>
          </tr>

          <!-- Filter row -->
          <tr>
            <th v-for="col in columns" :key="col.key">
              <template v-if="!col.type || col.type === 'text'">
                <Input v-model="columnFilters[col.key]" placeholder="Filter…" class="w-full" />
              </template>
              <template v-else-if="col.type === 'number'">
                <input type="number" v-model="columnFilters[col.key]" class="border rounded p-1 text-sm w-full" />
              </template>
              <template v-else-if="col.type === 'date'">
                <input type="date" v-model="columnFilters[col.key]" class="border rounded p-1 text-sm w-full" />
              </template>
              <template v-else-if="col.type === 'boolean'">
                <select v-model="columnFilters[col.key]" class="border rounded p-1 text-sm w-full">
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </template>
              <template v-else-if="col.type === 'select'">
                <select v-model="columnFilters[col.key]" class="border rounded p-1 text-sm w-full">
                  <option value="">All</option>
				  <option
				    v-for="opt in (col.options || dynamicOptions[col.key] || []).slice().sort((a, b) => a.label.localeCompare(b.label))"
				    :key="opt.id"
				    :value="opt.id"
				  >
				    {{ opt.label }}
				  </option>
                </select>
              </template>
            </th>
            <th v-if="enableediting"/>
          </tr>
        </thead>

        <tbody>
            <tr
              v-for="row in paginatedRows"
              :key="row.id"
              :class="['hover:bg-gray-50 odd:bg-white even:bg-gray-50 transition-colors', row._isNew ? 'new-row' : '']"
			        style="height: 20px!important"
            >
              <td v-for="col in columns" :key="col.key" class="align-middle p-2" :style="{width: colWidth(col), height: '20px!important', textAlign: col.textAlign ? col.textAlign : 'center'}">
                <div v-if="editingRow === row.id && col.editable">
                  <template v-if="!col.type || col.type === 'text'">
                    <Input :modelValue="getNested(editBuffer, col.key)" @update:modelValue="val => setNested(editBuffer, col.key, val)" />
                  </template>
                  <template v-else-if="col.type === 'number'">
                    <input type="number" :value="getNested(editBuffer, col.key)" @input="setNested(editBuffer, col.key, $event.target.valueAsNumber)" class="border rounded p-1 text-sm w-full" />
                  </template>
                  <template v-else-if="col.type === 'color'">
                    <input type="color" :value="getNested(editBuffer, col.key)" @input="setNested(editBuffer, col.key, $event.target.value)" class="h-8 w-12 p-0 border-none bg-transparent" />
                  </template>
                  <template v-else-if="col.type === 'date'">
                    <input type="date" :value="getNested(editBuffer, col.key)" @input="setNested(editBuffer, col.key, $event.target.value)" class="border rounded p-1 text-sm w-full" />
                  </template>
                  <template v-else-if="col.type === 'boolean'">
                    <input type="checkbox" :checked="getNested(editBuffer, col.key)" @change="setNested(editBuffer, col.key, $event.target.checked)" />
                  </template>
				          <template v-if="col.type === 'textarea'">
					          <textarea :value="getNested(editBuffer, col.key)" @input="setNested(editBuffer, col.key, $event.target.value)" rows="3" class="border rounded p-1 text-sm w-full resize-none"/>
                  </template>
                  <template v-else-if="col.type === 'select'">
                    <select :value="getNested(editBuffer, col.key)" @input="setNested(editBuffer, col.key, $event.target.value)" class="border rounded p-1 text-sm w-full">
                      <option v-for="opt in col.options || dynamicOptions[col.key] || []" :key="opt.id" :value="opt.id">
                        {{ opt.label }}
                      </option>
                    </select>
                  </template>
                  <p v-if="validationErrors[col.key]" class="text-xs text-red-500 mt-1">{{ validationErrors[col.key] }}</p>
                </div>

                <div v-else>
                  <template v-if="col.type === 'boolean'">
                    <span class="px-2 py-1 text-xs rounded-full font-medium" :class="getNested(row, col.key) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                      {{ getNested(row, col.key) ? 'Yes' : 'No' }}
                    </span>
                  </template>
				          <template v-else-if="col.type === 'textarea'">
				            <div class="relative group" style="text-align: left">
				              <span class="block truncate" :title="getNested(row, col.key)">
				              {{ getNested(row, col.key) }}
				              </span>
				              <div class="absolute z-50 hidden group-hover:block bg-white border shadow-lg p-2 max-w-xs break-words">
				              {{ getNested(row, col.key) }}
				              </div>
				            </div>
				          </template>
                  <template v-else-if="col.type === 'select'" style="text-align: left">
                    {{
                      (col.options || dynamicOptions[col.key] || []).find(opt => opt.id == getNested(row, col.key))?.label || getNested(row, col.key)
                    }}
                  </template>

                  <template v-else-if="col.type === 'color'">
                    <div class="flex items-center gap-2">
                      <div class="w-4 h-4 rounded-full border" :style="{ backgroundColor: getNested(row, col.key) }"></div>
                      <span class="text-xs text-gray-600">{{ getNested(row, col.key) }}</span>
                    </div>
                  </template>

                  <template v-else-if="col.type === 'date'">
                    {{
                      getNested(row, col.key)
                        ? new Date(getNested(row, col.key)).toLocaleDateString("de-DE", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : ""
                    }}
                  </template>

                  <template v-else style="text-align: left">
                    {{ getNested(row, col.key) }}
                  </template>
                </div>
              </td>

              <td v-if="enableediting" class="flex gap-2 justify-center p-2">
                <template v-if="editingRow === row.id">
                  <Button size="icon" variant="ghost" class="text-green-600 hover:bg-green-50" @click="saveRow(row)"><Save class="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" class="text-gray-600 hover:bg-gray-50" @click="cancelEdit(row)"><X class="w-4 h-4" /></Button>
                </template>
                <template v-else>
                  <Button size="icon" variant="ghost" class="text-blue-600 hover:bg-blue-50" @click="startEdit(row)"><Pencil class="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" class="text-red-600 hover:bg-red-50" @click="deleteRow(row.id)"><Trash class="w-4 h-4" /></Button>
                </template>
              </td>
            </tr>
          <!-- </TransitionGroup> -->
        </tbody>
      </table>
    </div>

	<div class="table-footer flex justify-between items-center" style="height:3.5rem; flex-shrink:0; background:white; padding:0 1rem;">
	   <div class="flex gap-2 items-center">
	     <label class="text-sm">Rows per page:</label>
	     <select v-model.number="pageSize" class="border rounded p-1 text-sm">
	       <option :value="10">10</option>
	       <option :value="20">20</option>
	       <option :value="50">50</option>
	     </select>
	   </div>

	   <div class="flex gap-2 items-center">
	     <Button :disabled="currentPage === 1" size="sm" @click="currentPage--">Prev</Button>
	     <span class="text-sm">Page {{ currentPage }} of {{ totalPages }}</span>
	     <Button :disabled="currentPage === totalPages" size="sm" @click="currentPage++">Next</Button>
	   </div>
	 </div>
  </Card>
</template>

<style>
.data-table-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  -webkit-text-size-adjust: 100%;
}

.data-table-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.data-table-scroll {
  flex: 1 1 auto;
  min-height: 0;              /* critical for Safari */
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.table-wrapper {
  flex: 1 1 auto;
  min-height: 0;                 /* critical for Safari */
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.table-footer {
  flex-shrink: 0;
  height: 3.5rem;                /* fixed height so Safari does not clip */
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}


thead tr:first-child th {
  position: sticky;
  top: 0;
  z-index: 30;
  background: #f3f4f6;
}

thead tr:nth-child(2) th {
  position: sticky;
  top: 3rem;
  z-index: 25;
  background: #f3f4f6;
}

table, tr, th, td {
  border: none !important;
}

th {
	height: 50px;
	max-width: 50px;
}
table {
  border-collapse: collapse; /* optional for cleaner borders */
  width: 100%;
  table-layout: auto;
}

td {
  max-height: 50px;       
  height: 50px;           
  padding: 8px;
  overflow: hidden;       
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
  text-align: center

}



/* Optional: remove transitions while testing Safari issues */
.fade-slide-enter-active,
.fade-slide-leave-active { transition: all 0.3s ease; }
.fade-slide-enter-from { opacity: 0; transform: translateY(-10px); }
.fade-slide-enter-to { opacity: 1; transform: translateY(0); }
.fade-slide-leave-from { opacity: 1; transform: translateY(0); }
.fade-slide-leave-to { opacity: 0; transform: translateY(10px); }

/* Highlight new row */
.new-row { animation: highlight 1s ease; }
@keyframes highlight { 0% { background-color: #e0f2fe; } 100% { background-color: transparent; } }
</style>
